export function formatDate(value: string | null | undefined, locale: string, withTime = false): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    ...(withTime ? { timeStyle: "short" as const } : {}),
  }).format(date);
}

export function formatMoney(
  minorUnits: string | null | undefined,
  currency: string,
  locale: string,
): string {
  const raw = minorUnits ?? "0";
  const parsed = Number.parseInt(raw, 10);
  const amount = Number.isNaN(parsed) ? 0 : parsed / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency === "" ? "USD" : currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function toMajorInput(minorUnits: string | null): string {
  if (minorUnits === null || minorUnits === "") {
    return "";
  }
  const parsed = Number.parseInt(minorUnits, 10);
  if (Number.isNaN(parsed)) {
    return "";
  }
  return (parsed / 100).toFixed(2);
}

export function fromMajorInput(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "0";
  }
  const parsed = Number.parseFloat(trimmed.replace(",", "."));
  if (Number.isNaN(parsed) || parsed < 0) {
    return "0";
  }
  return Math.round(parsed * 100).toString();
}

export function formatAmount(minorUnits: string | null | undefined, locale: string): string {
  const raw = minorUnits ?? "0";
  const parsed = Number.parseInt(raw, 10);
  const amount = Number.isNaN(parsed) ? 0 : parsed / 100;
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

export function formatQuantity(milliUnits: string | null | undefined, locale: string): string {
  const raw = milliUnits ?? "0";
  const parsed = Number.parseInt(raw, 10);
  const quantity = Number.isNaN(parsed) ? 0 : parsed / 1000;
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(quantity);
  } catch {
    return String(quantity);
  }
}

export function toMajorQuantity(milliUnits: string | null | undefined): string {
  if (milliUnits === null || milliUnits === undefined || milliUnits === "") {
    return "";
  }
  const parsed = Number.parseInt(milliUnits, 10);
  if (Number.isNaN(parsed)) {
    return "";
  }
  return String(parsed / 1000);
}

export function fromMajorQuantity(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "0";
  }
  const parsed = Number.parseFloat(trimmed.replace(",", "."));
  if (Number.isNaN(parsed) || parsed <= 0) {
    return "0";
  }
  return Math.round(parsed * 1000).toString();
}

export function formatPercent(rateBp: number, locale: string, scale = 100): string {
  const value = rateBp / scale;
  try {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)}%`;
  } catch {
    return `${value}%`;
  }
}

export function fromPercentToBp(value: string): number {
  const trimmed = value.trim();
  if (trimmed === "") {
    return 0;
  }
  const parsed = Number.parseFloat(trimmed.replace(",", "."));
  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }
  return Math.round(parsed * 100);
}
